/**
 * LightroomListTemplate - Minimal photo template with rating display
 *
 * Designed for the Lightroom-style horizontal filmstrip layout in critique mode.
 * Based on PhotoOnlyTemplate but:
 * - No orphan detection (not relevant in critique context)
 * - Adds compact rating display overlay
 *
 * Feature: 649-critique-feedback-options (Rating display in templates)
 *
 * @module PhotoItem2/templates/LightroomListTemplate
 */

import { cn } from '@/lib/utils';
import type { PhotoItem2TemplateProps } from '../PhotoItem2.types';
import { AdaptivePhoto } from '../../photos/AdaptivePhoto';
import { CompactRatingDisplay } from '../../composed/CompactRatingDisplay';
import { SelectionIndicator } from '@/components/atoms';

/**
 * LightroomListTemplate component
 *
 * Renders a minimal photo view with rating overlay.
 * Uses AdaptivePhoto for automatic resolution selection based on container size.
 *
 * Shows:
 * - Photographers: Average rating from critics
 * - Critics: Their own rating
 */
export function LightroomListTemplate({
  data,
  isSelected = false,
  selectable = false,
  zoom = 1.0,
  onClick,
  onCritiqueClick: _onCritiqueClick, // Filter out non-DOM prop
  className,
  ...restProps
}: PhotoItem2TemplateProps) {
  void zoom; // Suppress unused variable warning - AdaptivePhoto uses container size instead
  void _onCritiqueClick; // Filter out - not a valid DOM prop

  // Feature 081: photo-versioning - Forward additional props (e.g., onContextMenu) to div
  return (
    <div
      onClick={onClick}
      className={cn(
        'relative flex flex-col overflow-hidden rounded-lg bg-surface-2 w-full h-full',
        selectable && 'cursor-pointer hover:ring-2 hover:ring-primary/50',
        isSelected && 'ring-4 ring-primary',
        className
      )}
      {...restProps}
    >
      {/* Adaptive photo with automatic resolution selection */}
      <AdaptivePhoto
        photo={data.photoData}
        alt={data.photoData.description || ''}
        className="w-full h-full p-0.5"
        preferStorageUrl={true}
      />

      {/* Rating overlay - shows average for photographers, own rating for critics */}
      <CompactRatingDisplay
        photoId={data.photoData.id}
        size={12}
        variant="overlay"
      />

      {/* Selection indicator */}
      <SelectionIndicator visible={isSelected} size="md" className="z-10" />
    </div>
  );
}

LightroomListTemplate.displayName = 'LightroomListTemplate';
